import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Locales from 'locales';
import CategoriesTable from 'components/shared/CategoriesTable';
import AgencyCard from 'components/shared/AgencyCard';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';

const SelectAgencyModal = ({
  isOpened = false,
  categories = [],
  agencies = [],
  selectedAgency = null,
  setSelectedAgency = () => {},
  selectedCategories = [],
  setSelectedCategories = () => {},
  refetchAgencies = () => {},
  onConfirmAgency = () => {},
  closeModal = () => {},
}) => {
  const [showAgencyStep, setShowAgencyStep] = useState(false);

  const title = showAgencyStep ? 'Choisissez une société agréée' : 'Choisissez vos catégories';

  const description = showAgencyStep
    ? 'Étape 2/2 : Sélectionnez la société qui vous accompagnera dans vos démarches.'
    : 'Étape 1/2 : Sélectionnez de 1 à 4 catégories applicables à vos pèlerins.';

  const primaryBtnLabel = showAgencyStep ? Locales.base.button.save : 'Continuer';

  const secondaryBtnLabel = showAgencyStep ? 'Précédent' : Locales.base.button.discard;

  const toggleAgency = (agencyId) => {
    setSelectedAgency((prev) => (prev === agencyId ? null : agencyId));
  };

  const isSelectedAgency = (agencyId) => selectedAgency === agencyId;

  const handleAgencyKeyDown = (event, index) => {
    if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(event.key)) return;
    event.preventDefault();
    const direction = ['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1;
    const nextIndex = (index + direction + agencies.length) % agencies.length;
    setSelectedAgency(agencies[nextIndex].id);
    event.currentTarget.parentElement.children[nextIndex].focus();
  };

  const onConfirmCategory = () => {
    refetchAgencies();
    setShowAgencyStep(true);
  };

  const disabledBtn = (showAgencyStep && !selectedAgency) || (!showAgencyStep && selectedCategories.length === 0);

  const handleNext = () => (showAgencyStep ? onConfirmAgency() : onConfirmCategory());

  const handlePrevious = () => (showAgencyStep ? setShowAgencyStep(false) : closeModal());

  return (
    <Dialog open={isOpened} onOpenChange={closeModal}>
      <DialogContent
        className={`flex max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] flex-col gap-0 overflow-hidden rounded-[14px] border-gray-200 bg-white p-0 font-sans shadow-2xl sm:max-h-[calc(100dvh-3rem)] ${
          showAgencyStep ? 'max-w-[680px]' : 'max-w-[1024px]'
        }`}
      >
        <DialogHeader className="shrink-0 space-y-1 border-b border-gray-200 px-4 py-4 pr-14 text-left sm:px-6 sm:py-5">
          <DialogTitle className="text-base font-semibold text-[#202d28]">{title}</DialogTitle>
          <DialogDescription className="text-[13px] text-gray-500">{description}</DialogDescription>
          {showAgencyStep && (
            <div className="flex flex-wrap items-center gap-1.5 pt-3 text-xs">
              <span className="mr-1 text-[#6b7772]">Catégories sélectionnées</span>
              {selectedCategories.map((id) => {
                const category = categories.find((item) => item.id === id);
                return category ? (
                  <span key={id} className="rounded-md border border-[#e0e7e3] bg-[#f7f9f8] px-2 py-1 text-[#202d28]">
                    {category.name}
                  </span>
                ) : null;
              })}
            </div>
          )}
        </DialogHeader>

        {!showAgencyStep ? (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <CategoriesTable
              categories={categories}
              isSelectable
              isPilgrimUser
              variant="comparison"
              selectedCategories={selectedCategories}
              setSelectedCategories={setSelectedCategories}
            />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
            <p className="mb-3 text-[13px] text-[#6b7772]">
              {agencies.length === 1
                ? '1 société agréée disponible'
                : `${agencies.length} sociétés agréées disponibles`}
            </p>

            <div role="radiogroup" aria-label="Choisissez une seule société agréée" className="grid gap-2">
              {agencies.map((agency, index) => (
                <AgencyCard
                  key={agency.id}
                  agency={agency}
                  selected={isSelectedAgency(agency.id)}
                  onSelect={() => toggleAgency(agency.id)}
                  onKeyDown={(event) => handleAgencyKeyDown(event, index)}
                />
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="shrink-0 flex-col border-t border-gray-200 px-4 py-3 sm:flex-col sm:space-x-0 sm:px-6">
          {showAgencyStep && (
            <p className="mb-3 text-left text-xs text-[#6b7772]">
              Après validation, la société agréée ne pourra plus être modifiée.
            </p>
          )}
          <div className={`flex gap-3 ${showAgencyStep ? 'justify-between' : 'justify-end'}`}>
            <Button variant="outline" onClick={handlePrevious}>
              {secondaryBtnLabel}
            </Button>
            <Button variant="hajj" onClick={handleNext} disabled={disabledBtn}>
              {primaryBtnLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

SelectAgencyModal.propTypes = {
  isOpened: PropTypes.bool,
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  agencies: PropTypes.arrayOf(PropTypes.shape({})),
  selectedAgency: PropTypes.string,
  setSelectedAgency: PropTypes.func,
  selectedCategories: PropTypes.arrayOf(PropTypes.string),
  setSelectedCategories: PropTypes.func,
  refetchAgencies: PropTypes.func,
  onConfirmAgency: PropTypes.func,
  closeModal: PropTypes.func,
};

export default SelectAgencyModal;
